import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TabsContent } from '@/components/ui/tabs';
import useDepartments from '@/hooks/useDepartments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BasicTab({ formData, setFormData, formErrors, t, language, direction, isEdit, generatePassword, selectedDept }) {
  const [employees, setEmployees] = useState([]);
  const [departments] = useDepartments();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/employees`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setEmployees(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const managerOptions = employees.filter(e => e.id && e.email !== formData.email);
  return (
    <TabsContent value="basic" className="mt-3 space-y-3 h-[480px] overflow-y-auto pe-1">
      {/* Personal Info Section */}
      <div className="bg-white rounded-lg border border-[#C4BFF0] p-3 space-y-2">
        <p className="text-xs font-semibold text-[#655dc6]/70 tracking-wide">{t('personal_info')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('employee_name')}</Label>
            <Input
              className={formErrors.name ? 'border-red-500' : ''}
              value={formData.name}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^[\u0600-\u06FF\s\d.,]+$/.test(v)) setFormData(prev => ({ ...prev, name: v }));
              }}
              data-testid="input-name"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('employee_name_en')}</Label>
            <Input
              dir="ltr"
              className={`text-left ${formErrors.name_en ? 'border-red-500' : ''}`}
              value={formData.name_en}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^[a-zA-Z\s.\-]+$/.test(v)) setFormData(prev => ({ ...prev, name_en: v }));
              }}
              data-testid="input-name-en"
            />
          </div>
        </div>
      </div>

      {/* Account Section */}
      {!isEdit && (
        <div className="bg-white rounded-lg border border-[#C4BFF0] p-3 space-y-2">
          <p className="text-xs font-semibold text-[#655dc6]/70 tracking-wide">{t('account_info')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-start block">{t('email')}</Label>
              <Input
                type="email" dir="ltr"
                className={`text-left ${formErrors.email ? 'border-red-500' : ''}`}
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="example@lucent.com.sa"
                data-testid="input-email"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-start block">{t('password')}</Label>
              <div className="flex gap-2">
                <Input
                  dir="ltr"
                  className={`text-left flex-1 ${formErrors.password ? 'border-red-500' : ''}`}
                  value={formData.password}
                  readOnly
                  data-testid="input-password"
                />
                <Button
                  type="button" variant="outline" size="sm"
                  className="shrink-0 text-[#655dc6] border-[#655dc6] hover:bg-[#E8E6F5]"
                  onClick={() => setFormData(prev => ({ ...prev, password: generatePassword() }))}
                  data-testid="generate-password-btn"
                >
                  {t('generate')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Department & Position Section */}
      <div className="bg-white rounded-lg border border-[#C4BFF0] p-3 space-y-2">
        <p className="text-xs font-semibold text-[#655dc6]/70 tracking-wide">{t('work_info')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('department')}</Label>
            <Select value={formData.department} onValueChange={(v) => setFormData(prev => ({ ...prev, department: v, sub_department: '' }))} dir={direction}>
              <SelectTrigger className={formErrors.department ? 'border-red-500' : ''} data-testid="input-department"><SelectValue placeholder={t('department')} /></SelectTrigger>
              <SelectContent>
                {departments.map(d => (
                  <SelectItem key={d.value} value={d.value}>{language === 'en' ? d.label_en : d.label_ar}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedDept && selectedDept.subs.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-start block">{t('sub_department')}</Label>
              <Select value={formData.sub_department} onValueChange={(v) => setFormData(prev => ({ ...prev, sub_department: v }))} dir={direction}>
                <SelectTrigger data-testid="input-sub-department"><SelectValue placeholder={t('sub_department')} /></SelectTrigger>
                <SelectContent>
                  {selectedDept.subs.map(s => (
                    <SelectItem key={s.value} value={s.value}>{language === 'en' ? s.label_en : s.label_ar}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('position')}</Label>
            <Input
              className={formErrors.position ? 'border-red-500' : ''}
              value={formData.position}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^[\u0600-\u06FF\s\d.,]+$/.test(v)) setFormData(prev => ({ ...prev, position: v }));
              }}
              data-testid="input-position"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('position_en')}</Label>
            <Input
              className={formErrors.position_en ? 'border-red-500' : ''}
              value={formData.position_en}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^[a-zA-Z\s\d.,\-]+$/.test(v)) setFormData(prev => ({ ...prev, position_en: v }));
              }}
              placeholder="e.g. Dentist"
              data-testid="input-position-en"
              dir="ltr"
            />
          </div>
        </div>

        {/* Direct Manager (org chart) — replaces "is_department_manager" checkbox */}
        <div className="space-y-1 mt-1">
          <Label className="text-xs text-start block">
            {language === 'ar' ? 'المدير المباشر' : 'Direct Manager'}
          </Label>
          <Select
            value={formData.is_general_manager ? '__none__' : (formData.manager_id || '__none__')}
            onValueChange={(v) => setFormData(prev => ({ ...prev, manager_id: v === '__none__' ? '' : v }))}
            disabled={!!formData.is_general_manager}
            dir={direction}
          >
            <SelectTrigger
              data-testid="input-manager-id"
              className={formData.is_general_manager ? 'opacity-60 cursor-not-allowed' : ''}
            >
              <SelectValue placeholder={language === 'ar' ? 'اختر المدير المباشر' : 'Select direct manager'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">
                {language === 'ar' ? '— بدون مدير مباشر —' : '— No direct manager —'}
              </SelectItem>
              {managerOptions.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>
                  {language === 'ar' ? emp.name : (emp.name_en || emp.name)}
                  {emp.position ? ` — ${language === 'ar' ? emp.position : (emp.position_en || emp.position)}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formData.is_general_manager ? (
            <p className="text-[10px] text-[#D97706] font-medium" data-testid="gm-manager-hint">
              {language === 'ar'
                ? 'المدير العام بطبيعته بأعلى مستوى ولا يخضع لمدير مباشر. هذا الحقل مُعطّل تلقائياً.'
                : 'The General Manager sits at the top of the hierarchy and reports to no one. This field is auto-disabled.'}
            </p>
          ) : (
            <p className="text-[10px] text-[#655dc6]/50">
              {language === 'ar'
                ? 'الموظف الذي يَعتمد طلبات الإجازة لهذا الموظف. اتركه فارغاً إذا كان هذا الموظف بأعلى مستوى ولا يخضع لمدير. لتمييزه كمدير عام، فعّل خيار "مدير عام" أسفل.'
                : 'The employee who approves this person\'s requests. Leave empty if this employee is at the top with no manager. To mark them as General Manager, enable the "General Manager" option below.'}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 mt-1">
          <input
            type="checkbox"
            id="is_department_manager"
            checked={!!formData.is_department_manager}
            onChange={(e) => setFormData(prev => ({ ...prev, is_department_manager: e.target.checked }))}
            className="h-4 w-4 rounded border-[#C4BFF0] text-[#655dc6] focus:ring-[#655dc6]"
            data-testid="input-department-manager"
          />
          <Label htmlFor="is_department_manager" className="text-xs text-[#655dc6] cursor-pointer">
            {language === 'ar' ? 'مدير قسم (طلباته تذهب مباشرة للمدير العام)' : 'Department Manager (requests go straight to GM)'}
          </Label>
        </div>

        <div className="flex items-center gap-2 mt-1">
          <input
            type="checkbox"
            id="is_finance_manager"
            checked={!!formData.is_finance_manager}
            onChange={(e) => setFormData(prev => ({ ...prev, is_finance_manager: e.target.checked }))}
            className="h-4 w-4 rounded border-[#C4BFF0] text-[#655dc6] focus:ring-[#655dc6]"
            data-testid="input-finance-manager"
          />
          <Label htmlFor="is_finance_manager" className="text-xs text-[#655dc6] cursor-pointer">
            {language === 'ar' ? 'مدير مالي (سلطة اعتماد المخالصات المالية)' : 'Finance Manager (clearance finance approver)'}
          </Label>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="checkbox"
            id="is_general_manager"
            checked={!!formData.is_general_manager}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              is_general_manager: e.target.checked,
              // When marking as GM, auto-clear manager_id (a GM has no
              // direct manager). Preserved when un-ticking.
              manager_id: e.target.checked ? '' : prev.manager_id,
            }))}
            className="h-4 w-4 rounded border-[#C4BFF0] text-[#655dc6] focus:ring-[#655dc6]"
            data-testid="input-general-manager"
          />
          <Label htmlFor="is_general_manager" className="text-xs text-[#655dc6] cursor-pointer">
            {language === 'ar' ? 'مدير عام (المعتمد الأخير قبل الإدارة)' : 'General Manager (top of approval chain)'}
          </Label>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="checkbox"
            id="is_government_officer"
            checked={!!formData.is_government_officer}
            onChange={(e) => setFormData(prev => ({ ...prev, is_government_officer: e.target.checked }))}
            className="h-4 w-4 rounded border-indigo-400 text-indigo-600 focus:ring-indigo-500"
            data-testid="input-government-officer"
          />
          <Label htmlFor="is_government_officer" className="text-xs text-indigo-700 cursor-pointer">
            {language === 'ar'
              ? 'موظف علاقات حكومية (إدخال ورفع مستندات فقط — بدون حذف)'
              : 'Government Officer (create & upload docs only — no delete)'}
          </Label>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="checkbox"
            id="is_contracted_worker"
            checked={!!formData.is_contracted_worker}
            onChange={(e) => setFormData(prev => ({ ...prev, is_contracted_worker: e.target.checked }))}
            className="h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
            data-testid="input-contracted-worker"
          />
          <Label htmlFor="is_contracted_worker" className="text-xs text-amber-700 cursor-pointer">
            {language === 'ar'
              ? 'متعاقد عبر مورد قوى عاملة (مستثنى من بدلات الإجازة والمخالصة النقدية)'
              : 'Contracted via manpower supplier (excluded from leave allowance & cash settlement)'}
          </Label>
        </div>

        {/* Doctor flag — only meaningful for the Medical department */}
        <div className="mt-1 p-2 rounded-md border border-blue-200 bg-blue-50/50 space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_doctor"
              checked={!!formData.is_doctor}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                is_doctor: e.target.checked,
                // Reset doctor_type when toggled off
                doctor_type: e.target.checked ? (prev.doctor_type || 'salary') : '',
              }))}
              className="h-4 w-4 rounded border-blue-400 text-blue-600 focus:ring-blue-500"
              data-testid="input-is-doctor"
            />
            <Label htmlFor="is_doctor" className="text-xs text-blue-800 cursor-pointer">
              {language === 'ar'
                ? 'طبيب (الإدارة الطبية — لا يلزمه تسجيل حضور/انصراف)'
                : 'Doctor (Medical Dept — exempt from attendance punch)'}
            </Label>
          </div>
          {formData.is_doctor && (
            <div className="ms-6 flex flex-wrap items-center gap-4">
              <Label className="text-xs text-blue-800 font-medium">
                {language === 'ar' ? 'نوع التعاقد:' : 'Doctor type:'}
              </Label>
              <label className="inline-flex items-center gap-1 text-xs text-blue-900 cursor-pointer">
                <input
                  type="radio"
                  name="doctor_type"
                  value="salary"
                  checked={formData.doctor_type === 'salary'}
                  onChange={() => setFormData(prev => ({ ...prev, doctor_type: 'salary' }))}
                  className="text-blue-600"
                  data-testid="input-doctor-type-salary"
                />
                {language === 'ar' ? 'طبيب براتب (بدلات كاملة)' : 'Salary (full benefits)'}
              </label>
              <label className="inline-flex items-center gap-1 text-xs text-blue-900 cursor-pointer">
                <input
                  type="radio"
                  name="doctor_type"
                  value="percentage"
                  checked={formData.doctor_type === 'percentage'}
                  onChange={() => setFormData(prev => ({ ...prev, doctor_type: 'percentage' }))}
                  className="text-blue-600"
                  data-testid="input-doctor-type-percentage"
                />
                {language === 'ar' ? 'طبيب بالنسبة (إجازات للاطلاع فقط)' : 'Percentage (leaves for awareness only)'}
              </label>
            </div>
          )}
        </div>
      </div>
    </TabsContent>
  );
}
